import { shallow } from "zustand/shallow";

import {
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import useVideoStore from "@app/analytics/MeetingReport/stores/video";

import Stack, { Row } from "@rui/Stack";
import Switch from "@rui/Switch";

import useVideoControls from "./useVideoControls";

const SettingLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "0.875rem",
  fontWeight: 500,
}));

const PlaybackSpeedButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButton-root": {
    border: "none",
    fontFamily: theme.typography.fontFamilySecondary,
    color: "#FFF",
    borderRadius: "8px !important",
    padding: "2px 6px",
    backgroundColor: "transparent",
    "&:not(:first-of-type)": {
      marginLeft: theme.spacing(0.5),
    },
    "&:hover": {
      backgroundColor: "#FFFFFF1A",
    },

    "&.Mui-selected": {
      color: "#FFF",
      backgroundColor: theme.palette.shades.primary[500],
      "&:hover": {
        backgroundColor: theme.palette.shades.primary[500],
      },
    },
  },
}));

const VideoPlayerSettings = () => {
  const theme = useTheme();

  const { updatePlaybackRate } = useVideoControls();

  const {
    highlightAnnotationsEnabled,
    metricAnnotationsEnabled,
    playbackRate,
    toggleHighlightAnnotations,
    toggleMetricAnnotations,
  } = useVideoStore(
    (state) => ({
      highlightAnnotationsEnabled: state.highlightAnnotationsEnabled,
      metricAnnotationsEnabled: state.metricAnnotationsEnabled,
      playbackRate: state.playbackRate,
      toggleHighlightAnnotations: state.toggleHighlightAnnotations,
      toggleMetricAnnotations: state.toggleMetricAnnotations,
    }),
    shallow
  );

  const handlePlaybackRateChange = (
    event: React.MouseEvent<HTMLElement>,
    rate: number
  ) => {
    if (rate) {
      updatePlaybackRate(rate);
    }
  };

  return (
    <Stack spacing={1.5} variant="left" sx={{ minWidth: "268px" }}>
      <Row variant="apart" sx={{ width: "100%" }}>
        <SettingLabel>Show metrics on screen</SettingLabel>
        <Switch
          sx={{ m: 0 }}
          checked={metricAnnotationsEnabled}
          onChange={toggleMetricAnnotations}
          uncheckedColor={theme.palette.shades.neutral[50]}
          uncheckedThumbColor={theme.palette.shades.neutral[400]}
        />
      </Row>
      <Row variant="apart" sx={{ width: "100%" }}>
        <SettingLabel>Show highlights on screen</SettingLabel>
        <Switch
          sx={{ m: 0 }}
          checked={highlightAnnotationsEnabled}
          onChange={toggleHighlightAnnotations}
          uncheckedColor={theme.palette.shades.neutral[50]}
          uncheckedThumbColor={theme.palette.shades.neutral[400]}
        />
      </Row>
      <Row variant="apart" sx={{ width: "100%" }}>
        <SettingLabel>Playback speed</SettingLabel>
        <PlaybackSpeedButtonGroup
          value={playbackRate}
          onChange={handlePlaybackRateChange}
          exclusive
        >
          <ToggleButton value={1}>1x</ToggleButton>
          <ToggleButton value={1.5}>1.5x</ToggleButton>
          <ToggleButton value={2}>2x</ToggleButton>
        </PlaybackSpeedButtonGroup>
      </Row>
    </Stack>
  );
};

export default VideoPlayerSettings;
